import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { UserCircleIcon, KeyIcon } from '@heroicons/react/24/outline';
import { useAuthStore } from '../stores/authStore';
import { profileApi } from '../services/api';
import { useDialog } from '../hooks/useDialog';

const ProfilePage: React.FC = () => {
  const { t } = useTranslation(['auth', 'common']);
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const { alert } = useDialog();
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await profileApi.updateProfile(formData);
      await useAuthStore.getState().getCurrentUser();
      setIsEditing(false);
      await alert(t('auth:saveProfileSuccess'), t('auth:updateSuccess'), 'success');
    } catch (error: any) {
      await alert(error.response?.data?.message || t('auth:saveProfileFailed'), t('auth:updateFailed'), 'danger');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto bg-bg-page min-h-full">
      <div className="bg-bg-surface rounded-lg shadow-sm border border-border-subtle p-6 mb-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 bg-info/20 rounded-full flex items-center justify-center">
            <UserCircleIcon className="w-8 h-8 text-info" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-50">{t('auth:profile')}</h1>
            <p className="text-gray-400">{t('auth:profileSubtitle')}</p>
          </div>
        </div>

        {isEditing ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">{t('auth:profileName')}</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-border-subtle rounded-lg focus:ring-2 focus:ring-info focus:border-transparent bg-bg-form text-gray-50"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">{t('auth:profileEmail')}</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-3 py-2 border border-border-subtle rounded-lg focus:ring-2 focus:ring-info focus:border-transparent bg-bg-form text-gray-50"
                required
              />
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setIsEditing(false)}
                className="px-4 py-2 border border-border-subtle rounded-lg hover:bg-bg-elevated text-gray-300 transition-colors cursor-pointer"
              >
                {t('common:cancel')}
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-info hover:bg-info/80 text-white rounded-lg disabled:opacity-50 transition-colors cursor-pointer"
              >
                {loading ? t('auth:saving') : t('auth:save')}
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">{t('auth:profileName')}</label>
                <p className="text-gray-50">{user?.name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">{t('auth:profileEmail')}</label>
                <p className="text-gray-50">{user?.email}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">{t('auth:profileRole')}</label>
                <p className="text-gray-50">{user?.role}</p>
              </div>
            </div>
            <button
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 bg-info hover:bg-info/80 text-white rounded-lg transition-colors cursor-pointer"
            >
              {t('auth:editProfile')}
            </button>
          </div>
        )}
      </div>

      <div className="bg-bg-surface rounded-lg shadow-sm border border-border-subtle p-6">
        <h2 className="text-lg font-semibold text-gray-50 mb-4">{t('auth:securitySettings')}</h2>
        <div className="border border-border-subtle rounded-lg p-4 flex justify-between items-center">
          <div>
            <h3 className="font-medium text-gray-50">{t('auth:changePassword')}</h3>
            <p className="text-sm text-gray-400">{t('auth:changePasswordDesc')}</p>
          </div>
          <button
            onClick={() => navigate('/change-password')}
            className="flex items-center gap-2 px-4 py-2 bg-success hover:bg-success/80 text-white rounded-lg transition-colors cursor-pointer"
          >
            <KeyIcon className="w-4 h-4" />
            {t('auth:changePassword')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
